import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  AlertTriangle, Shield, TrendingUp, TrendingDown, Users, FileText,
  ChevronRight, Star, AlertCircle, CheckCircle2, Clock, Settings,
  Plus, Trash2, Award, MessageSquare, ArrowUpRight, Briefcase
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  info: 'bg-blue-100 text-blue-700',
  low: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};
const ENTRY_TYPE_ICONS = {
  warning: AlertTriangle, commendation: Award, client_feedback: MessageSquare,
  escalation: AlertCircle, note: FileText, disciplinary: Shield, recognition: Star,
};
const BACKLOG_COLORS = { 1: 'bg-amber-500', 2: 'bg-orange-500', 3: 'bg-red-600' };

export default function EscalationDashboardPage() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [configDirty, setConfigDirty] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selected201, setSelected201] = useState(null);
  const [file201, setFile201] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ employee_id: '', entry_type: 'note', title: '', description: '', severity: 'info' });

  const fetchDashboard = useCallback(async () => {
    try {
      const [dash, conf, emps] = await Promise.allSettled([
        axios.get(`${API}/api/escalation/dashboard`),
        axios.get(`${API}/api/escalation/config`),
        axios.get(`${API}/api/hr/employees?limit=200`),
      ]);
      if (dash.status === 'fulfilled') setDashboard(dash.value.data);
      else {
        setDashboard({ backlog_summary: { level_1: 0, level_2: 0, level_3: 0, total: 0 }, backlog_jobs: [], recent_escalations: [], top_warnings: [], top_performers: [], config: {} });
        console.error('Dashboard API error:', dash.reason?.response?.status, dash.reason?.message);
      }
      if (conf.status === 'fulfilled') setConfig(conf.value.data);
      else setConfig({ enabled: true, backlog_1_threshold: 2, backlog_2_threshold: 3, backlog_3_threshold: 5, auto_email_manager: true, auto_email_hr: true, auto_201_entry: true, backlog_3_action: 'warning' });
      if (emps.status === 'fulfilled') setEmployees(emps.value.data || []);
    } catch (e) { console.error('Escalation load error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetch201 = async (empId) => {
    try {
      const { data } = await axios.get(`${API}/api/escalation/201/${empId}`);
      setFile201(data);
      setSelected201(empId);
    } catch { toast.error('Failed to load 201 file'); }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/api/escalation/config`, config);
      toast.success('Configuration saved');
      setConfigDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const addEntry = async () => {
    if (!entryForm.employee_id || !entryForm.title.trim()) { toast.error('Employee and title required'); return; }
    try {
      await axios.post(`${API}/api/escalation/201`, entryForm);
      toast.success('Entry added to 201 file');
      setShowAddEntry(false);
      setEntryForm({ employee_id: '', entry_type: 'note', title: '', description: '', severity: 'info' });
      if (selected201 === entryForm.employee_id) fetch201(selected201);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add entry'); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this 201 entry?')) return;
    try {
      await axios.delete(`${API}/api/escalation/201/${entryId}`);
      toast.success('Entry deleted');
      if (selected201) fetch201(selected201);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="escalation-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Escalation & 201 File<PageInfoTooltip pageKey="escalation" /></h1>
            <p className="text-xs text-gray-400">Track backlog, escalations, and employee performance records</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAddEntry(true)} data-testid="add-201-entry-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Entry</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="dashboard" className="text-xs gap-1.5" data-testid="tab-dashboard"><AlertCircle className="h-3.5 w-3.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="201files" className="text-xs gap-1.5" data-testid="tab-201files"><FileText className="h-3.5 w-3.5" />201 Files</TabsTrigger>
          <TabsTrigger value="config" className="text-xs gap-1.5" data-testid="tab-config"><Settings className="h-3.5 w-3.5" />Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          {dashboard && <DashboardView data={dashboard} onView201={fetch201} />}
        </TabsContent>

        <TabsContent value="201files">
          <File201View employees={employees} selected={selected201} file={file201}
            onSelect={fetch201} onDelete={deleteEntry} />
        </TabsContent>

        <TabsContent value="config">
          {config && <ConfigView config={config} setConfig={(c) => { setConfig(c); setConfigDirty(true); }}
            onSave={saveConfig} dirty={configDirty} />}
        </TabsContent>
      </Tabs>

      {/* Add Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add 201 File Entry</DialogTitle>
            <DialogDescription>Manually log a warning, commendation, or note to an employee's personnel file.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={entryForm.employee_id} onValueChange={v => setEntryForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="entry-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={entryForm.entry_type} onValueChange={v => setEntryForm(p => ({ ...p, entry_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="entry-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['warning', 'commendation', 'note', 'disciplinary', 'recognition', 'client_feedback'].map(t =>
                      <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Severity</Label>
                <Select value={entryForm.severity} onValueChange={v => setEntryForm(p => ({ ...p, severity: v }))}>
                  <SelectTrigger className="mt-1" data-testid="entry-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['info', 'low', 'medium', 'high', 'critical'].map(s =>
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Title</Label>
              <Input value={entryForm.title} onChange={e => setEntryForm(p => ({ ...p, title: e.target.value }))} placeholder="Brief title" className="mt-1" data-testid="entry-title-input" /></div>
            <div><Label className="text-xs">Description</Label>
              <Textarea value={entryForm.description} onChange={e => setEntryForm(p => ({ ...p, description: e.target.value }))} placeholder="Details..." className="mt-1 min-h-[80px]" data-testid="entry-desc-input" /></div>
            <Button className="w-full" onClick={addEntry} data-testid="submit-entry-btn">Add Entry</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DashboardView({ data, onView201 }) {
  const { backlog_summary: bs, backlog_jobs, recent_escalations, top_warnings, top_performers } = data;

  return (
    <div className="space-y-5">
      {/* Backlog Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Backlog', value: bs.total, color: 'bg-gray-100 text-gray-700', icon: Briefcase },
          { label: 'Level 1', value: bs.level_1, color: 'bg-amber-50 text-amber-700', icon: Clock },
          { label: 'Level 2', value: bs.level_2, color: 'bg-orange-50 text-orange-700', icon: AlertTriangle },
          { label: 'Level 3 Critical', value: bs.level_3, color: 'bg-red-50 text-red-700', icon: AlertCircle },
        ].map(item => (
          <Card key={item.label} className="bg-white border-gray-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", item.color.split(' ')[0])}>
                  <item.icon className={cn("h-4 w-4", item.color.split(' ')[1])} />
                </div>
                <span className="text-xs text-gray-500">{item.label}</span>
              </div>
              <span className="text-2xl font-bold text-text-primary">{item.value}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Backlogged Jobs */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Backlogged Jobs</CardTitle></CardHeader>
          <CardContent>
            {backlog_jobs.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No backlogged jobs</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {backlog_jobs.map(job => (
                  <div key={job.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid={`backlog-job-${job.id}`}>
                    <div className={cn("w-2 h-8 rounded-full flex-shrink-0", BACKLOG_COLORS[job.backlog_level] || 'bg-gray-300')} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{job.title}</p>
                      <p className="text-[10px] text-gray-400">{job.assigned_name || 'Unassigned'} &middot; {job.assignment_count || 1}x reassigned</p>
                    </div>
                    <Badge className={cn("text-[9px]",
                      job.backlog_level === 3 ? 'bg-red-100 text-red-700' :
                      job.backlog_level === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-amber-100 text-amber-700'
                    )}>BL {job.backlog_level}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Escalations */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertCircle className="h-4 w-4 text-red-500" />Recent Escalations</CardTitle></CardHeader>
          <CardContent>
            {recent_escalations.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No recent escalations</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {recent_escalations.map(e => {
                  const Icon = ENTRY_TYPE_ICONS[e.entry_type] || FileText;
                  return (
                    <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100">
                      <Icon className="h-4 w-4 text-red-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{e.title}</p>
                        <p className="text-[10px] text-gray-400">{e.created_at?.split('T')[0]} &middot; {e.source || 'manual'}</p>
                      </div>
                      <Badge className={cn("text-[9px]", SEVERITY_COLORS[e.severity] || 'bg-gray-100')}>{e.severity}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top Warnings */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="h-4 w-4 text-red-500" />Most Warnings</CardTitle></CardHeader>
          <CardContent>
            {top_warnings.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No warnings recorded</p>
            ) : (
              <div className="space-y-2">
                {top_warnings.map(tw => (
                  <div key={tw.employee_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 cursor-pointer hover:border-red-200 transition-colors"
                    onClick={() => onView201(tw.employee_id)} data-testid={`warning-emp-${tw.employee_id}`}>
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600">{tw.count}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tw.name}</p>
                      <p className="text-[10px] text-gray-400">{tw.department}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" />Top Performers</CardTitle></CardHeader>
          <CardContent>
            {top_performers.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No commendations yet</p>
            ) : (
              <div className="space-y-2">
                {top_performers.map(tp => (
                  <div key={tp.employee_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 cursor-pointer hover:border-emerald-200 transition-colors"
                    onClick={() => onView201(tp.employee_id)} data-testid={`performer-emp-${tp.employee_id}`}>
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-600">{tp.count}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tp.name}</p>
                      <p className="text-[10px] text-gray-400">{tp.department}</p>
                    </div>
                    <Star className="h-4 w-4 text-amber-400" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function File201View({ employees, selected, file, onSelect, onDelete }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
      {/* Employee List */}
      <Card className="bg-white border-gray-border lg:col-span-1">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Employees</CardTitle></CardHeader>
        <CardContent className="max-h-[500px] overflow-y-auto">
          <div className="space-y-1">
            {employees.map(emp => (
              <button key={emp.id} onClick={() => onSelect(emp.id)}
                className={cn("w-full text-left p-2.5 rounded-lg border transition-colors",
                  selected === emp.id ? "bg-amber-50 border-amber-200" : "border-transparent hover:bg-gray-50"
                )} data-testid={`emp-201-${emp.id}`}>
                <p className="text-sm font-medium">{emp.first_name} {emp.last_name}</p>
                <p className="text-[10px] text-gray-400">{emp.position || emp.department}</p>
              </button>
            ))}
            {employees.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No employees found</p>}
          </div>
        </CardContent>
      </Card>

      {/* 201 File Content */}
      <div className="lg:col-span-3 space-y-4">
        {!file ? (
          <Card className="bg-white border-gray-border"><CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">Select an employee to view their 201 file</p>
          </CardContent></Card>
        ) : (
          <>
            {/* Employee Summary */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-text-primary">{file.employee.name}</h3>
                    <p className="text-xs text-gray-500">{file.employee.position} &middot; {file.employee.department}</p>
                    {file.employee.reports_to && <p className="text-[10px] text-gray-400 mt-0.5">Reports to: {file.employee.reports_to}</p>}
                  </div>
                  <div className="flex gap-2">
                    {[
                      { label: 'Warnings', value: file.stats.warnings, color: 'bg-red-100 text-red-700' },
                      { label: 'Commendations', value: file.stats.commendations, color: 'bg-emerald-100 text-emerald-700' },
                      { label: 'Escalations', value: file.stats.escalations, color: 'bg-orange-100 text-orange-700' },
                      { label: 'Feedback', value: file.stats.client_feedbacks, color: 'bg-blue-100 text-blue-700' },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold", s.color)}>{s.value}</div>
                        <p className="text-[9px] text-gray-500 mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Backlog Jobs */}
            {file.backlog_jobs?.length > 0 && (
              <Card className="bg-white border-red-200">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Active Backlog Jobs</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {file.backlog_jobs.map(j => (
                      <div key={j.id} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 text-sm">
                        <div className={cn("w-1.5 h-6 rounded-full", BACKLOG_COLORS[j.backlog_level] || 'bg-gray-300')} />
                        <span className="flex-1 truncate">{j.title}</span>
                        <Badge className="text-[9px] bg-red-100 text-red-600">BL{j.backlog_level} &middot; {j.assignment_count || 1}x</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Entries */}
            <Card className="bg-white border-gray-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Personnel Records ({file.stats.total})</CardTitle></CardHeader>
              <CardContent>
                {file.entries.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">No records in this employee's 201 file</p>
                ) : (
                  <div className="space-y-2">
                    {file.entries.map(entry => {
                      const Icon = ENTRY_TYPE_ICONS[entry.entry_type] || FileText;
                      return (
                        <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid={`entry-${entry.id}`}>
                          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                            entry.entry_type === 'warning' || entry.entry_type === 'disciplinary' ? 'bg-red-100' :
                            entry.entry_type === 'commendation' || entry.entry_type === 'recognition' ? 'bg-emerald-100' :
                            entry.entry_type === 'escalation' ? 'bg-orange-100' : 'bg-blue-100'
                          )}>
                            <Icon className={cn("h-4 w-4",
                              entry.entry_type === 'warning' || entry.entry_type === 'disciplinary' ? 'text-red-600' :
                              entry.entry_type === 'commendation' || entry.entry_type === 'recognition' ? 'text-emerald-600' :
                              entry.entry_type === 'escalation' ? 'text-orange-600' : 'text-blue-600'
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium">{entry.title}</p>
                              <Badge className={cn("text-[9px]", SEVERITY_COLORS[entry.severity] || 'bg-gray-100')}>{entry.severity}</Badge>
                              <Badge className="text-[9px] bg-gray-100 text-gray-500 capitalize">{entry.entry_type.replace('_', ' ')}</Badge>
                            </div>
                            {entry.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{entry.description}</p>}
                            <p className="text-[10px] text-gray-400 mt-1">
                              {entry.created_at?.split('T')[0]} &middot; by {entry.created_by_name || entry.created_by} &middot; {entry.source}
                            </p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 flex-shrink-0"
                            onClick={() => onDelete(entry.id)} data-testid={`delete-entry-${entry.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}


function ConfigView({ config, setConfig, onSave, dirty }) {
  const update = (key, val) => setConfig({ ...config, [key]: val });

  return (
    <div className="max-w-2xl space-y-5">
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Settings className="h-4 w-4 text-gray-500" />Escalation Settings</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-3 rounded-xl border border-gray-border bg-gray-50">
            <div><p className="text-sm font-bold">Enable Escalation System</p><p className="text-[10px] text-gray-400">Auto-track backlog levels and escalate to managers</p></div>
            <Switch checked={config.enabled} onCheckedChange={v => update('enabled', v)} data-testid="toggle-escalation" />
          </div>

          <div>
            <Label className="text-xs font-bold">Backlog Thresholds (Reassignment Count)</Label>
            <p className="text-[10px] text-gray-400 mb-2">Number of times a job gets reassigned to trigger each backlog level</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                <Label className="text-xs text-amber-700 font-bold">Level 1</Label>
                <Input type="number" min={1} value={config.backlog_1_threshold}
                  onChange={e => update('backlog_1_threshold', parseInt(e.target.value) || 1)} className="mt-1 h-8" data-testid="threshold-1" />
              </div>
              <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                <Label className="text-xs text-orange-700 font-bold">Level 2</Label>
                <Input type="number" min={2} value={config.backlog_2_threshold}
                  onChange={e => update('backlog_2_threshold', parseInt(e.target.value) || 2)} className="mt-1 h-8" data-testid="threshold-2" />
              </div>
              <div className="p-3 rounded-lg border border-red-200 bg-red-50">
                <Label className="text-xs text-red-700 font-bold">Level 3 (Critical)</Label>
                <Input type="number" min={3} value={config.backlog_3_threshold}
                  onChange={e => update('backlog_3_threshold', parseInt(e.target.value) || 3)} className="mt-1 h-8" data-testid="threshold-3" />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-border">
              <div><p className="text-xs font-bold">Auto-email Manager on Escalation</p></div>
              <Switch checked={config.auto_email_manager} onCheckedChange={v => update('auto_email_manager', v)} data-testid="toggle-email-manager" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-border">
              <div><p className="text-xs font-bold">Auto-email HR on Escalation</p></div>
              <Switch checked={config.auto_email_hr} onCheckedChange={v => update('auto_email_hr', v)} data-testid="toggle-email-hr" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-border">
              <div><p className="text-xs font-bold">Auto-log to 201 File</p></div>
              <Switch checked={config.auto_201_entry} onCheckedChange={v => update('auto_201_entry', v)} data-testid="toggle-auto-201" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold">Backlog Level 3 Auto-Action</Label>
            <Select value={config.backlog_3_action} onValueChange={v => update('backlog_3_action', v)}>
              <SelectTrigger className="mt-1" data-testid="bl3-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warning">Issue Warning</SelectItem>
                <SelectItem value="suspension">Suspension Notice</SelectItem>
                <SelectItem value="review">Performance Review Required</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dirty && <Button className="w-full" onClick={onSave} data-testid="save-config-btn">Save Configuration</Button>}
        </CardContent>
      </Card>
    </div>
  );
}
